import React, { ReactElement, ElementType } from 'react'
import { Svg } from 'ui'
import { ThemeUIStyleObject } from 'theme-ui'
import { ButtonWithLoadingState } from 'components/ButtonWithLoadingState'
import ArrowIcon from 'images/button-arrow.svg'
import ExternalSvgIcon, {
  colorFilters,
  ExternalIcon,
  globeIcon,
} from 'components/Dashboard/CommonComponents/ExternalSvgIcon'
import useGetColorMode from 'utils/useGetColorMode'

export enum ActionType {
  BLOCK = 'block',
  BYPASS = 'bypass',
  REDIRECT = 'redirect',
}

interface ActionButtonProps {
  isActive: boolean
  type: string
  // use icon for block and bypass types
  icon?: ElementType
  isSwitchChecked?: boolean
  isService?: boolean
  // use externalIcon for the redirect type
  externalIcon?: ExternalIcon
  svgStyle?: ThemeUIStyleObject
  testId?: string
  isLoading?: boolean
  showArrow?: boolean
  onClick?: () => void
  disabled?: boolean
  ariaLabel?: string
  // these methods are used only in service cards
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  onTouchStart?: () => void
  onTouchEnd?: () => void
}
export default function ActionButton({
  onClick,
  isSwitchChecked,
  isService,
  icon,
  type,
  isActive,
  svgStyle,
  disabled = false,
  testId,
  isLoading,
  externalIcon,
  showArrow,
  ariaLabel,
  onMouseEnter,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
}: ActionButtonProps): ReactElement {
  const { isLightMode } = useGetColorMode()

  // different cases for the redirect type
  const isGlobeIcon = externalIcon?.icon === globeIcon // if no location is selected, we display globeIcon
  const isAutoOrRedirect = externalIcon?.icon === 'N/A' || externalIcon?.icon === '?' // selected auto/redirect
  const isLocationSelected = !!externalIcon?.icon && !isGlobeIcon // selected auto or redirect or country
  const isCountrySelected = isLocationSelected && !isAutoOrRedirect // selected only country (excluded auto/redirect)

  return (
    <ButtonWithLoadingState
      isLoading={isLoading}
      variant={isActive ? 'transparent' : 'simple'}
      ariaLabel={ariaLabel || `${type} button`}
      aria-checked={isActive}
      data-testid={testId || `${type.toLowerCase()}-button`}
      data-test-checked={isActive}
      tabIndex={isSwitchChecked ? 0 : -1}
      disabled={disabled}
      role="menuitemradio"
      spinnerColor="greenApple"
      sxSpinner={{
        m: 0,
        height: '2.4rem',
        width: '2.4rem',
      }}
      sx={{
        width: `3.2rem`,
        height: `3.2rem`,
        zIndex: 'zIndex50',
        flexShrink: 0,
        p: 0,
        borderRadius: '10rem',
        '& svg,path': {
          fill: 'aliceBlue60',
        },
        // hover event is not available for the active action button
        '&:hover': {
          ':not([disabled])': {
            '& svg,path': {
              fill: 'aliceBlue' || 'initial',
              opacity: 1,
            },
            '& img': {
              filter: !isCountrySelected && !isLightMode ? colorFilters.white : undefined,
            },
          },
        },
        ':disabled': {
          cursor: 'auto',
          backgroundColor: 'transparent',
          opacity: '0.6',
        },
        // styles for the active action button
        ...(isActive && {
          backgroundColor: type === ActionType.BLOCK ? 'pomegranate' : 'greenApple',
          border: '1px solid',
          borderColor: 'blueYonder30',
          '& svg,path': {
            fill: 'blue800',
          },
          '&:hover': undefined,
          ':disabled': {
            '& svg,path': {
              fill: type === ActionType.BLOCK ? 'pomegranate' : 'greenApple',
            },
            backgroundColor: 'white6',
            borderColor: 'blueYonder30',
          },
        }),
        // certain styles only for the service card
        ...(isService && getServiceStyles(isSwitchChecked, isLocationSelected)),
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {icon && (
        <Svg
          svg={icon}
          descriptiveText={`${type} ${isActive ? 'active' : 'inactive'} action button`}
          sx={{ width: '1.2rem', height: '1.2rem', ...svgStyle }}
        />
      )}

      {externalIcon && (
        <ExternalSvgIcon
          ignoreDefaultLightModeFilter
          icon={externalIcon.icon}
          type={externalIcon.type}
          // country icon should have undefined fill
          fill={isCountrySelected ? undefined : getExternalIconColor(isLightMode, isActive)}
          sx={{ width: '1.2rem', height: '1.2rem' }}
          descriptiveText={`${isSwitchChecked ? 'Selected' : 'Auto'} Location Icon`}
        />
      )}

      {isActive && showArrow && (
        <Svg
          svg={ArrowIcon}
          sx={{
            height: '0.8rem',
            width: '0.8rem',
          }}
          descriptiveText="show redirect arrow icon"
        />
      )}
    </ButtonWithLoadingState>
  )
}

function getExternalIconColor(isLightMode?: boolean, isActive?: boolean) {
  if (isLightMode) {
    return isActive ? 'aliceBlue' : 'black'
  }

  return isActive ? 'black' : 'aliceBlue60'
}

function getServiceStyles(isSwitchChecked?: boolean, isLocationSelected?: boolean) {
  if (isSwitchChecked) {
    return {}
  }

  return {
    backgroundColor: isLocationSelected ? 'aliceBlue60SilverSpoon' : 'transparent',
    border: 'none',
    '& svg,path': {
      fill: 'aliceBlue60',
    },
  }
}
